import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import Laptop from "@mui/icons-material/Laptop";
import * as Yup from "yup";
import { useCallback, useEffect, useMemo, useState } from "react";
import usePickup from "hooks/usePickup";
import { Link, useParams } from "react-router-dom";
import { Modal } from "components/common/Modal";
import {
  Build,
  BusinessOutlined,
  Check,
  DeveloperBoard,
  Domain,
  EventOutlined,
  Inventory,
  Mediation,
  PublishedWithChanges,
  Replay,
  SyncLock,
} from "@mui/icons-material";
import { userHasPermission } from "utils/userPermissions";
import useCurrentUser from "hooks/useCurrentUser";
import PickupStateChip from "components/PickupState";
import { format, parseISO } from "date-fns";
import { ElasticDataGrid } from "components/DataGrid/RevivnElasticDataGrid";
import useSWR from "swr";
import { Pickup } from "types/pickup";
import { columns } from "components/Inventories/DataGrid/datagridColumns";
import { debounce } from "lodash";
import { api } from "services/api.service";
import { GridBulkPrintActionItem } from "components/Inventories/DataGrid/GridBulkPrintActionItem";
import { GridBulkActionsItem } from "components/DataGrid/components/GridBulkActionsItem";
import DeleteIcon from "@mui/icons-material/Delete";
import { BulkActions } from "components/Inventories/DataGrid/BulkActions";
import useInventoryTasks from "hooks/useTasks";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ActionProgressDrawer from "components/Inventories/ActionProgressDrawer";
import { FullPagePaperLayout } from "pages/Layouts/FullPagePaperLayout";
import { PageActions, PageBreadcrumbs, PageSubtitle, PageTitle } from "pages/Layouts/components/PageTitleContainer";
import { PagePaperPreamble } from "pages/Layouts/components/PagePaperPreamble";
import { MetaList, MetaListItem } from "pages/Layouts/components/MetaList";
import { useFeatureFlag } from "hooks/useFeatureFlag";

interface IRModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  pickupUuid: string;

  handleIrReady(): void;
}

export function IRModal({ open, setOpen, pickupUuid, handleIrReady }: IRModalProps) {
  const [loading, setLoading] = useState(false);
  const [countableInventoryCount, setCountableInventoryCount] = useState<number>();
  const [noAssetsConfirmed, setNoAssetsConfirmed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setCountableInventoryCount(undefined);
    setNoAssetsConfirmed(false);
    api
      .get<Pickup>(`/pickups/${pickupUuid}`)
      .then(({ data }) => setCountableInventoryCount(data.countableInventoryCount))
      .catch(() => setCountableInventoryCount(undefined))
      .finally(() => setLoading(false));
  }, [open, pickupUuid]);

  const noAssets = countableInventoryCount === 0;

  return (
    <Modal open={open} setOpen={setOpen} width={"683px"}>
      <Box mt={4}>
        <Box sx={{ textAlign: "center" }}>
          <Check color="primary" sx={{ fontSize: "60px" }} />
          <Typography mt={1} variant="h5" component="h2" fontWeight={800} fontSize={"24px"}>
            Initial Report Ready{" "}
          </Typography>
          <Typography mt={1} color="text.secondary" fontSize={"16px"}>
            Are you sure you want to proceed with publishing the initial report? (Note: The user(s) will receive an
            email notification and see this reflected under their pickup.){" "}
          </Typography>
          {noAssets && (
            <Alert severity="warning" sx={{ mt: 3, textAlign: "left" }}>
              This pickup has no devices to serialize, so the initial report will list no devices. Publish only if
              everything on this pickup is e-waste.
              <FormControlLabel
                sx={{ display: "flex", mt: 1 }}
                control={
                  <Checkbox checked={noAssetsConfirmed} onChange={(e) => setNoAssetsConfirmed(e.target.checked)} />
                }
                label="I confirm there are no assets to serialize"
              />
            </Alert>
          )}

          <Box mt={4.5} sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "center" }}>
            <Button
              variant="contained"
              disabled={loading || (noAssets && !noAssetsConfirmed)}
              onClick={() => handleIrReady()}
            >
              Yes, publish
            </Button>
            <Button variant="contained" onClick={() => setOpen(false)}>
              no, go back
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

interface FRModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;

  handleFrReady(): void;

  handleForceFrReady(): void;
}

export function FRModal({ open, setOpen, handleFrReady, handleForceFrReady }: FRModalProps) {
  const [bypassOnHold, setBypassOnHold] = useState(false);

  const publish = useCallback(() => {
    if (bypassOnHold) {
      handleForceFrReady();
    } else {
      handleFrReady();
    }
    // I don't think we should bypass eslint exhaustive deps here, but this is how it worked before
    // so leaving for now.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleFrReady, bypassOnHold]);

  return (
    <Modal open={open} setOpen={setOpen} width={"683px"}>
      <Box mt={4}>
        <Box sx={{ textAlign: "center" }}>
          <Check color="primary" sx={{ fontSize: "60px" }} />
          <Typography mt={1} variant="h5" component="h2" fontWeight={800} fontSize={"24px"}>
            Final Report Ready
          </Typography>
          <Typography mt={1} color="text.secondary" fontSize={"16px"}>
            Are you sure you want to proceed with publishing the final report? (Note: The user(s) will receive an email
            notification and see this reflected under their pickup.)
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: 3,
            }}
          >
            <FormControlLabel
              control={<Checkbox checked={bypassOnHold} onChange={(e) => setBypassOnHold(e.target.checked)}></Checkbox>}
              label={
                <Typography color="text.secondary" fontSize={"16px"}>
                  Bypass 'On Hold' state and complete pickup
                </Typography>
              }
            />
          </Box>

          <Box mt={4.5} sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "center" }}>
            <Button variant="contained" onClick={publish}>
              Yes, publish
            </Button>
            <Button variant="contained" onClick={() => setOpen(false)}>
              no, go back
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

function InventoryTasksButton() {
  const [isDrawerOpen, toggleDrawer] = useState(false);
  const { createInventoryTasks } = useInventoryTasks();

  const handleClick = () => {
    toggleDrawer(!isDrawerOpen);
    createInventoryTasks();
  };

  return (
    <div>
      <Button startIcon={<ListAltIcon />} onClick={handleClick}>
        View Actions
      </Button>
      {isDrawerOpen ? <ActionProgressDrawer toggleDrawer={toggleDrawer} isDrawerOpen={isDrawerOpen} /> : null}
    </div>
  );
}

export function ElasticPickupInventoriesPage() {
  const params = useParams();
  const { updatePickup, irReady, frReady, forceFrReady, enrollmentCheck } = usePickup();

  const { data: pickup } = useSWR<Pickup>(`/pickups/${params.pickupId}`);
  const [irOpen, setIrOpen] = useState(false);
  const [frOpen, setFrOpen] = useState(false);
  const [ewasteValue, setEwasteValue] = useState<Pickup["ewasteInLbs"]>(0);
  const [ewasteValueMissingError, setEwasteValueMissingError] = useState(false);
  const { currentUserPermissions } = useCurrentUser();
  const { enabled: pickupContainersPageEnabled } = useFeatureFlag("internal_pickup_containers_page");
  const [mdmChecked, setMdmChecked] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceInput = useCallback(
    debounce((wasteValue) => {
      if (!pickup) return;
      updatePickup({ ...pickup, ewasteInLbs: wasteValue });
    }, 1000),
    [pickup],
  );

  useEffect(() => {
    if (!pickup) return;
    setEwasteValue(pickup.ewasteInLbs);
  }, [pickup, setEwasteValue]);

  const canEnrollmentCheck = useMemo(() => {
    if (!pickup) return false;
    if (!currentUserPermissions.includes("warehouse_manager")) return false;
    const appleLaptopCount = pickup.appleLaptopCount || 0;
    return appleLaptopCount > 0 && pickup.state === "processing";
  }, [currentUserPermissions, pickup]);

  if (!pickup) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleEwasteChange = (e: any) => {
    setEwasteValueMissingError(false);
    setEwasteValue(e.target.value);
    debounceInput(e.target.value);
  };
  const handleIrReady = () => {
    irReady(pickup.uuid).then(() => setIrOpen(false));
  };

  const handleFrReady = () => {
    frReady(pickup.uuid).then(() => setFrOpen(false));
  };

  const handleForceFrReady = () => {
    forceFrReady(pickup.uuid).then(() => setFrOpen(false));
  };
  const macBackgroundColor = () => {
    const estimate = pickup.postPickupForms[0]?.appleLaptopCount;

    const actual = pickup.appleLaptopCount;
    if (estimate === actual) {
      return "#FFF";
    } else {
      return "#FFF2F1";
    }
  };
  const pcBackgroundColor = () => {
    const estimate = pickup.postPickupForms[0]?.pcLaptopCount;

    const actual = pickup.pcLaptopCount;
    if (estimate === actual) {
      return "#FFF";
    } else {
      return "#FFF2F1";
    }
  };

  // Date with timezone and full day with "at" joining date and time
  const friendlyPickupTime = format(parseISO(pickup.scheduledAt), "EEEE, MMMM d, yyyy 'at' h:mm aaa");

  return (
    <FullPagePaperLayout>
      <PageBreadcrumbs links={[{ label: "Warehouse" }, { label: "Pickups", path: "/pickups" }]} />
      <PageTitle>
        <Stack direction="row" gap={1} alignItems="center">
          {!pickup.repairPickup && !pickup.returnPickup && !pickup.rebuiltFromPartsPickup && (
            <>
              Pickup {pickup.id}
              <PickupStateChip pickupState={pickup.state} />
              {pickup.virtualPickup && <Chip label={"Virtual Pickup"} />}
            </>
          )}
          {pickup.repairPickup && (
            <>
              Return & Warranty Repairs
              <Chip
                label={<Replay sx={{ fontSize: 12 }} />}
                sx={{ padding: 0, backgroundColor: "rgba(242, 160, 43, 0.3)", color: "#D58031" }}
              />
            </>
          )}
          {pickup.returnPickup && (
            <>
              Redeploy Inventory
              <Chip
                label={<Mediation sx={{ fontSize: 12 }} />}
                sx={{ padding: 0, backgroundColor: "rgba(106, 104, 207, 0.2)", color: "#6A68CF" }}
              />
            </>
          )}
          {pickup.rebuiltFromPartsPickup && (
            <>
              Rebuilt from Parts
              <Chip
                label={<Build sx={{ fontSize: 12 }} />}
                sx={{ padding: 0, backgroundColor: "rgba(1, 131, 183, 0.3)", color: "#005577" }}
              />
            </>
          )}
          {pickup.indsPartsPickup && (
            <>
              INDS Parts
              <Chip
                label={<DeveloperBoard sx={{ fontSize: 12 }} />}
                sx={{ padding: 0, backgroundColor: "rgba(242, 160, 43, 0.3)", color: "#D58031" }}
              />
            </>
          )}
          {pickup.internalItAssetsPickup && (
            <>
              Internal IT Assets
              <Chip
                label={<Domain sx={{ fontSize: 12 }} />}
                sx={{ padding: 0, backgroundColor: "rgba(227, 202, 108, 0.2)", color: "#E3CA6C" }}
              />
            </>
          )}
        </Stack>
      </PageTitle>
      <PageSubtitle>
        <MetaList>
          <MetaListItem title="Organization" label={pickup.organization.name} icon={<BusinessOutlined />} />
          <MetaListItem title="Pickup Date" label={friendlyPickupTime} icon={<EventOutlined />} />
        </MetaList>
      </PageSubtitle>
      <PageActions>
        <Stack direction="row" gap={1} alignItems="center">
          <FormControl>
            <TextField
              id="ewasteInLbs"
              name="ewasteInLbs"
              value={ewasteValue}
              size="small"
              label="E-waste"
              autoComplete="off"
              onChange={handleEwasteChange}
              slotProps={{
                input: { endAdornment: <InputAdornment position="end">lbs</InputAdornment> },
              }}
              sx={{ width: 120 }}
              error={ewasteValueMissingError}
              helperText={ewasteValueMissingError && "E-waste should be either 0 or a positive"}
            />
          </FormControl>
          <Button component={Link} variant="contained" startIcon={<Laptop />} to="inventories/new">
            Add Items
          </Button>
          <Button component={Link} variant="contained" startIcon={<AddAPhotoIcon />} to={"add-photos/scan"}>
            Add Photos
          </Button>
          {pickupContainersPageEnabled && (
            <Button component={Link} startIcon={<Inventory />} variant="contained" to="containers">
              Container List
            </Button>
          )}
          {canEnrollmentCheck && (
            <Tooltip
              placement="top-end"
              title="Start a high priority bulk enrollment check for all MacOS devices without an enrollment status in this pickup."
            >
              <Button
                disabled={mdmChecked}
                variant="contained"
                startIcon={<SyncLock />}
                onClick={() => enrollmentCheck(pickup.uuid).then(() => setMdmChecked(true))}
              >
                MDM Check
              </Button>
            </Tooltip>
          )}
        </Stack>
      </PageActions>
      <PagePaperPreamble>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {userHasPermission("warehouse_manager", currentUserPermissions) && (
            <>
              {!pickup.repairPickup &&
                !pickup.returnPickup &&
                !pickup.rebuiltFromPartsPickup &&
                !pickup.indsPartsPickup && (
                  <Box display="flex" flexDirection="row" gap={1}>
                    <Button
                      startIcon={<PublishedWithChanges />}
                      onClick={() => {
                        if (Yup.number().min(0).isValidSync(ewasteValue)) {
                          setIrOpen(true);
                        } else {
                          setEwasteValueMissingError(true);
                        }
                      }}
                      variant="contained"
                      disabled={!!pickup.initialReportPublishedAt}
                    >
                      Publish IR{" "}
                      {pickup.initialReportPublishedAt && format(parseISO(pickup.initialReportPublishedAt), "MM/dd/yy")}
                    </Button>
                    <IRModal open={irOpen} setOpen={setIrOpen} pickupUuid={pickup.uuid} handleIrReady={handleIrReady} />
                    <Button
                      startIcon={<PublishedWithChanges />}
                      onClick={() => {
                        setFrOpen(true);
                      }}
                      disabled={!!pickup.finalReportPublishedAt}
                      variant="contained"
                    >
                      Publish FR{" "}
                      {pickup.finalReportPublishedAt && format(parseISO(pickup.finalReportPublishedAt), "MM/dd/yy")}
                    </Button>
                    <FRModal
                      open={frOpen}
                      setOpen={setFrOpen}
                      handleFrReady={handleFrReady}
                      handleForceFrReady={handleForceFrReady}
                    />
                    <InventoryTasksButton />
                  </Box>
                )}
            </>
          )}
          <Stack direction="row" alignItems="center">
            <TextField
              label="Estimate Macs"
              size="small"
              style={{ width: 115, backgroundColor: macBackgroundColor() }}
              value={pickup.postPickupForms[0] && pickup.postPickupForms[0].appleLaptopCount}
              InputProps={{
                readOnly: true,
              }}
              focused
            />
            <TextField
              label="Processed Macs"
              size="small"
              style={{ marginLeft: 10, width: 115 }}
              value={pickup.appleLaptopCount}
              InputProps={{
                readOnly: true,
              }}
              focused
            />
            <TextField
              label="Estimate PCs"
              size="small"
              style={{ marginLeft: 10, width: 115, backgroundColor: pcBackgroundColor() }}
              value={pickup.postPickupForms[0] && pickup.postPickupForms[0].pcLaptopCount}
              InputProps={{
                readOnly: true,
              }}
              focused
            />
            <TextField
              label="Processed PCs"
              size="small"
              style={{ marginLeft: 10, width: 115 }}
              value={pickup.pcLaptopCount}
              InputProps={{
                readOnly: true,
              }}
              focused
            />
          </Stack>
        </Stack>
      </PagePaperPreamble>
      <BulkActions />
      <ElasticDataGrid
        bulkEditable={true}
        autoHeight={false}
        columns={columns}
        density={"compact"}
        checkboxSelection
        getBulkActions={() => [
          <GridBulkPrintActionItem />,
          <GridBulkActionsItem icon={<DeleteIcon />} eventName={"bulkDelete"} label={"Delete"} />,
        ]}
        syncStateWithSearchParams
        defaultToMostRecentParams
        isCellEditable={(cellParams) => {
          return cellParams.row.inventoryType === "internal";
        }}
        processBulkUpdate={(changes) => {
          const updates = Object.entries(changes).map(([id, update]) => ({ id, ...update }));
          return api.put("inventories/bulk/edit", { inventories: updates });
        }}
        persistSettingsKey={`pickup_inventories`}
        url={`pickups/${pickup.id}/combined_inventories`}
        initialState={{
          columns: {
            columnVisibilityModel: {
              printed: false,
              lineId: false,
              pickupId: false,
              tags: false,
              iqInventoryId: false,
              iqPoIdLine: false,
              warehouseId: false,
              manifestId: false,
              invoicedAt: false,
            },
          },
        }}
      />
    </FullPagePaperLayout>
  );
}
